import React, { useEffect, useState } from "react";
import styles from "./Register.module.scss";
import { AuthContainer } from "../../components/AuthContainer/AuthContainer";
import { Button } from "../../components";
import Checkbox from "../../components/ui/Checkbox/Checkbox";
import { InputPassword } from "../../components/ui/InputPassword/InputPassword";
import { Input } from "../../components/ui/Input/Input";
import { ROUTES } from "../../helpers/routes";
import { useAppDispatch, useAppSelector } from "../../services/hooks";
import { register } from "../../services/slices/user";
import ErrorMessage from "../../components/ErrorMessage/ErrorMessage";
import Loader from "../../components/Loader/Loader";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { DropdownTwo } from "../../components/ui/Dropdown/DropdownTwo";
import { USER_TYPES } from "../../types/user";
import { capitalizeFirstLetter } from "../../utils/utils";
import { ErrorPage } from "../ErrorPage/ErrorPage";

export const Register = () => {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [agree, setAgree] = useState(false);
  const dispatch = useAppDispatch();
  const { loading, error } = useAppSelector((state) => state.user);
  const [errorPassword, setErrorPassword] = useState<null | string>(null);
  const [errorFetch, setErrorFetch] = useState(false);
  const { user } = useAppSelector((state) => state.user);
  const [typeUser, setTypeUser] = useState<number | null>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const handleSubmit = async (event: React.FormEvent) => {
    if (loading) return;
    event.preventDefault();

    const type = USER_TYPES.find((type) => type.id === typeUser)?.name;

    if (!type) return <ErrorPage />;

    setErrorFetch(false);

    const userData = {
      username,
      password,
      email,
      firstName,
      lastName,
      type,
    };

    const resultAction = await dispatch(register(userData));

    setErrorPassword(null);
    if (password !== confirmPassword) {
      setErrorPassword("The passwords don't match");
      return;
    }

    if (register.fulfilled.match(resultAction)) {
      setErrorFetch(false);
      navigate("/");
      resetForm();
    } else {
      setErrorFetch(true);
    }
  };

  const resetForm = () => {
    setAgree(false);
    setUsername("");
    setFirstName("");
    setLastName("");
    setEmail("");
    setPassword("");
    setConfirmPassword("");
  };

  useEffect(() => {
    setErrorPassword(null);
  }, [password, setConfirmPassword]);

  if (user) <Navigate to={ROUTES.home} state={{ from: location }} replace />;

  return (
    <AuthContainer
      title="Sign up"
      subtitle="Let’s get you all set up so you can access your personal account."
    >
      {errorFetch && (
        <ErrorMessage
          message={
            error || "An error occurred during registration. Please try again."
          }
        />
      )}
      <form onSubmit={handleSubmit} className={styles.form}>
        <div className={styles.grid}>
          <div className={styles.grid__a}>
            <Input
              value={firstName}
              setValue={setFirstName}
              id="first-name"
              type="text"
              label="First Name"
              placeholder="John"
              required
            />
          </div>
          <div className={styles.grid__b}>
            <Input
              value={lastName}
              setValue={setLastName}
              id="last-name"
              type="text"
              label="Last Name"
              placeholder="Doe"
              required
            />
          </div>
          <div className={styles.grid__c}>
            <Input
              value={email}
              setValue={setEmail}
              id="email"
              type="email"
              label="Email"
              placeholder="example@mail.com"
              required
            />
          </div>
          <div className={styles.grid__d}>
            <Input
              value={username}
              setValue={setUsername}
              id="username"
              type="text"
              label="Username"
              placeholder="Enter your username"
              required
            />
          </div>
          <div className={styles.grid__e}>
            <InputPassword
              value={password}
              setValue={setPassword}
              id="password"
              label="Password"
              placeholder="Enter the password"
              required
              errorMessage={errorPassword}
            />
          </div>
          <div className={styles.grid__f}>
            <InputPassword
              value={confirmPassword}
              setValue={setConfirmPassword}
              id="confirm-password"
              label="Confirm Password"
              placeholder="Repeat the password"
              required
              errorMessage={errorPassword}
            />
          </div>
          <div className={styles.grid__h}>
            <DropdownTwo
              data={USER_TYPES.map((types) => ({
                id: types.id,
                name: capitalizeFirstLetter(types.name),
              }))}
              setState={setTypeUser}
              required
              name={"Wer sind Sie?"}
              state={
                capitalizeFirstLetter(
                  USER_TYPES.find((type) => type.id === typeUser)?.name,
                ) || null
              }
            />
          </div>
        </div>
        <div className={styles.loginOptions}>
          <Checkbox required checked={agree} onChange={setAgree} name={2}>
            I agree to all the{" "}
            <a className={styles.link} href="/">
              Terms
            </a>{" "}
            and{" "}
            <a className={styles.link} href="/">
              Privacy Policies
            </a>
          </Checkbox>
        </div>

        <Button type="submit" disabled={loading}>
          {loading ? (
            <>
              <Loader />
              Creating account...
            </>
          ) : (
            "Create account"
          )}
        </Button>
        <p className={styles.signupText}>
          Already have an account?{" "}
          <a className={styles.link} href={ROUTES.login}>
            Login
          </a>
        </p>
      </form>
    </AuthContainer>
  );
};
